import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState, useId } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { apiClient, handleApiError } from "@/lib/api-client";
import { authClient } from "@/lib/auth-client";
import {
	CheckCircle2,
	AlertCircle,
	Loader2,
	Smartphone,
	Monitor,
} from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";

export const Route = createFileRoute("/_layout/redeem")({
	component: RedeemPage,
	validateSearch: (search: Record<string, unknown>) => {
		return {
			code: (search.code as string) || "",
			message: (search.message as string) || undefined,
		};
	},
});

type RedeemStatus = "idle" | "redeeming" | "redeemed" | "error";

function RedeemPage() {
	const { code, message } = Route.useSearch();
	const navigate = useNavigate();
	const [status, setStatus] = useState<RedeemStatus>("idle");
	const [error, setError] = useState<string | null>(null);
	const { data: session } = authClient.useSession();
	const appleLogoTitleId = useId();

	// No validation on mount - optimistic UI
	const deepLinkUrl = `macrowave:///redeem?code=${code}`;

	const handleRedeem = async () => {
		if (!session) return;

		setStatus("redeeming");
		setError(null);

		try {
			const result = await apiClient.checkout.redeem(code);

			if (result.success && result.entitled) {
				setStatus("redeemed");
				// Redirect to home after a brief delay
				setTimeout(() => {
					navigate({ to: "/" });
				}, 5000);
			} else {
				setStatus("error");
				setError("Failed to redeem code. Please try again.");
			}
		} catch (err: any) {
			setStatus("error");

			// Handle specific error cases with helpful messages
			if (err.message?.includes("409")) {
				setError(
					"This code has already been used. Check your email for a discount code!",
				);
			} else if (err.message?.includes("400")) {
				setError(
					"This code doesn't exist. Double check the code and try again.",
				);
			} else {
				setError("Failed to redeem code. Please try again or contact support.");
			}

			handleApiError(err, "Failed to redeem code");
		}
	};

	// Check if code exists
	if (!code) {
		return (
			<div className="min-h-screen bg-white dark:bg-gray-950 flex items-center justify-center p-6">
				<Card className="p-12 max-w-md text-center border border-gray-200 dark:border-gray-800">
					<AlertCircle className="w-16 h-16 mx-auto mb-6 text-red-500" />
					<h2 className="text-2xl font-semibold mb-2 text-gray-900 dark:text-white">
						No Code Provided
					</h2>
					<p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
						This link is missing a redemption code.
					</p>
					<Button
						onClick={() => navigate({ to: "/" })}
						className="bg-gray-900 hover:bg-gray-800 text-white dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100"
					>
						Go to Home
					</Button>
				</Card>
			</div>
		);
	}

	// Successfully redeemed state
	if (status === "redeemed") {
		return (
			<div className="min-h-screen bg-white dark:bg-gray-950 flex items-center justify-center p-6">
				<Card className="p-12 max-w-md text-center border border-gray-200 dark:border-gray-800">
					<div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-50 dark:bg-green-950/30 mb-6">
						<CheckCircle2 className="w-10 h-10 text-green-600 dark:text-green-400" />
					</div>
					<h2 className="text-2xl font-semibold mb-2 text-gray-900 dark:text-white">
						Welcome to Lifetime Access!
					</h2>
					<p className="text-sm text-gray-500 dark:text-gray-400">
						Your access is now active. You can start broadcasting immediately.
					</p>
					<p>You will be redirected to main page now.</p>
				</Card>
			</div>
		);
	}

	// Main redeem page
	return (
		<div className="min-h-screen bg-white dark:bg-gray-950">
			<div className="container mx-auto max-w-2xl px-4 py-16">
				{/* Header */}
				<div className="text-center mb-8">
					<div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-50 dark:bg-green-950/30 mb-6">
						<CheckCircle2 className="w-10 h-10 text-green-600 dark:text-green-400" />
					</div>
					<h1 className="text-3xl font-semibold mb-2 text-gray-900 dark:text-white">
						{message || "Thank You!"}
					</h1>
					<p className="text-lg text-gray-600 dark:text-gray-400 mb-2">
						{status === "error"
							? "Redemption failed"
							: "Your payment was successful"}
					</p>
					<p className="text-sm text-gray-500 dark:text-gray-500">
						{status === "error"
							? error
							: "Scan the code below to claim your lifetime access"}
					</p>
				</div>

				{/* Error Message */}
				{status === "error" && (
					<Card className="p-6 mb-6 border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20">
						<div className="flex items-start gap-3">
							<AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
							<div>
								<p className="text-sm font-medium text-red-800 dark:text-red-200 mb-1">
									Redemption Failed
								</p>
								<p className="text-sm text-red-700 dark:text-red-300">
									{error}
								</p>
							</div>
						</div>
					</Card>
				)}

				{/* QR Code Section */}
				<Card className="p-12 mb-6 border border-gray-200 dark:border-gray-800">
					<div className="flex flex-col items-center">
						<QRCodeCanvas
							value={deepLinkUrl}
							size={256}
							level="H"
							includeMargin={true}
						/>
						<p className="text-sm text-gray-500 dark:text-gray-400 mt-6 text-center">
							Scan with your device to open Macrowave
						</p>
					</div>
				</Card>

				{/* Or Button */}
				<div className="text-center mb-6">
					<p className="text-sm text-gray-400 dark:text-gray-600 mb-4">or</p>

					<div className="flex flex-col sm:flex-row items-center justify-center gap-3">
						{session ? (
							<Button
								onClick={handleRedeem}
								disabled={status === "redeeming"}
								size="lg"
								className="w-full sm:w-auto"
							>
								{status === "redeeming" ? (
									<>
										<Loader2 className="w-4 h-4 mr-2 animate-spin" />
										Redeeming...
									</>
								) : (
									<>
										<Smartphone className="w-4 h-4 mr-2" />
										Redeem with @{session.user.username || session.user.name}
									</>
								)}
							</Button>
						) : (
							<button
								className="bg-black text-white py-3 px-5 rounded-lg font-serif hover:bg-gray-800 transition-colors duration-200 flex items-center justify-center gap-2 text-base w-full sm:w-auto"
								onClick={() => {
									const redirectPath = `/redeem?code=${code}${message ? `&message=${encodeURIComponent(message)}` : ""}`;
									navigate({
										to: "/signin",
										search: { redirect: redirectPath },
									});
								}}
								type="button"
							>
								<svg
									aria-labelledby={appleLogoTitleId}
									className="h-5 w-5"
									fill="currentColor"
									viewBox="0 0 24 24"
								>
									<title id={appleLogoTitleId}>Apple logo</title>
									<path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
								</svg>
								<span>Sign in with Apple to Redeem</span>
							</button>
						)}

						<button
							className="py-3 px-5 rounded-lg font-serif border border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors duration-200 flex items-center justify-center gap-2 text-base w-full sm:w-auto"
							onClick={() => {
								window.location.href = deepLinkUrl;
							}}
							type="button"
						>
							<Monitor className="h-5 w-5" />
							<span>Open App</span>
						</button>
					</div>
				</div>

				{/* Divider */}
				<div className="relative my-12">
					<div className="absolute inset-0 flex items-center">
						<div className="w-full border-t border-gray-200 dark:border-gray-800" />
					</div>
				</div>

				{/* App Store Download */}
				<Card className="p-8 border border-gray-200 dark:border-gray-800 mb-6">
					<div className="flex flex-col sm:flex-row items-center gap-6">
						<div className="flex-1">
							<h3 className="font-semibold text-lg mb-1 text-gray-900 dark:text-white">
								Don't have the app yet?
							</h3>
							<p className="text-sm text-gray-600 dark:text-gray-400">
								Download Macrowave to start broadcasting
							</p>
						</div>
						<a
							href="https://apps.apple.com/app/apple-store/id6746954963?pt=123127751&ct=macrowave.co&mt=8"
							target="_blank"
							rel="noopener noreferrer"
							className="flex-shrink-0"
						>
							<img
								src="/images/app-store-badge.svg"
								alt="Download on the App Store"
								className="h-12 w-auto"
							/>
						</a>
					</div>
				</Card>

				{/* Support */}
				<div className="text-center">
					<p className="text-sm text-gray-500 dark:text-gray-400">
						Need help?{" "}
						<a
							href="mailto:support@macrowave.co"
							className="text-gray-900 dark:text-white underline"
						>
							support@macrowave.co
						</a>
					</p>
				</div>
			</div>
		</div>
	);
}
